import { normalizeRoleName } from './roles';
import { resolveProfile, extraProfiles, isPlatformAdmin, isLegacyIdentity, isSalesIdentity, userLanding as profileUserLanding, type ProfileUser } from './profileIdentity';
import { profileMenu, profileMenuWithExtras } from './profileMenus';
import { PROFILE_WORKFLOW_ROUTES, PROFILE_DENIED_WORKFLOW_ROUTES, PLATFORM_ROUTES, routePatternMatches } from './profileRoutes';
import { REGISTERED_APP_ROUTES } from './registeredAppRoutes';
import { resolveRoleKey } from '../pages/dashboard/roleLayouts';
import { SECTIONS_BY_VIEW, roleKeyToView, withDealerManagementNavigation } from '../components/layout/sidebarRegistry';
import { dealerTierFor } from './roles';
export { resolveProfile, profileLanding, isPlatformAdmin, PROFILE_LABELS } from './profileIdentity';
export type { ProfileId, ProfileUser } from './profileIdentity';
export { profileMenu } from './profileMenus';
export type { ProfileMenuItem, ProfileMenuSection } from './profileMenus';


const UNIVERSAL_ROUTES = new Set(['/profile', '/account', '/support/my', '/no-access']);
const ALL_MENU_PATHS = new Set(Array.from({length: 18}, (_, i) => `P${String(i + 1).padStart(2, '0')}` as import('./profileIdentity').ProfileId)
  .flatMap(p => profileMenu(p).flatMap(s => s.items.map(i => i.to.split('?')[0]))));
const STATIC_ROUTES = new Set([...REGISTERED_APP_ROUTES.filter(p => !p.includes(':')), ...ALL_MENU_PATHS]);

export function normalizeAccountingTab(raw: string | null): string | null {
  if (!raw) return null;
  const tab = raw.trim().toLowerCase();
  return ({ ledgers: 'ledger', voucher: 'vouchers', pl: 'pnl', bs: 'balance', 'bank-recon': 'bankreco' } as Record<string, string>)[tab] ?? tab;
}

function normalizedPath(pathname: string): string | null {
  try {
    const decoded = decodeURIComponent(pathname);
    // Reject ambiguous encodings/segment separators rather than authorize a different router path.
    if (decoded !== pathname || !pathname.startsWith('/') || /[\\?#\s]/.test(pathname) || pathname.includes('//') || pathname.split('/').some(p => p === '.' || p === '..')) return null;
    return pathname.replace(/\/+$/, '') || '/';
  } catch { return null; }
}

/** The UI preview state is deliberately absent from this authorization input. */
export function canOpenProfileRoute(user: ProfileUser | null | undefined, pathname: string, search = ''): boolean {
  if (!user) return false;
  const path = normalizedPath(pathname);
  if (!path) return false;
  if (path === '/lead-activity') return isSalesIdentity(user);
  if (path === '/scorecard') {
    const targets = new URLSearchParams(search).getAll('userId');
    // P17 (HR) reads a rep's card from the Incentives page (2026-10-08); never its own "My Scorecard".
    const profile = resolveProfile(user);
    const oversight = (profile === 'P18' || profile === 'P17') && !!user.id && targets.length === 1 && !!targets[0] && targets[0] !== user.id;
    return isSalesIdentity(user) || oversight;
  }
  const platform = isPlatformAdmin(user);
  if (PLATFORM_ROUTES.includes(path)) return platform;
  if (UNIVERSAL_ROUTES.has(path)) return true;
  const profile = resolveProfile(user);
  const legacyRole = normalizeRoleName(user.role?.name);
  if (!profile && !isLegacyIdentity(user) && !platform) return false;
  // /dashboard is an alias: the landing component redirects synchronously before dashboard children mount.
  if (path === '/' || path === '/dashboard') return true;
  const items = profile
    ? profileMenuWithExtras(profile, extraProfiles(user)).flatMap(s => s.items)
    : withDealerManagementNavigation(SECTIONS_BY_VIEW[roleKeyToView(resolveRoleKey(user), legacyRole)], dealerTierFor(legacyRole))
      .flatMap(s => [...s.items, ...(s.seeMore ?? [])]);
  const allowed = new Set(items.map(i => i.to));
  if (path === '/accounting' || path === '/accounting/ledger') {
    const params = new URLSearchParams(search);
    const tabs = params.getAll('tab');
    if (tabs.length !== 1) return false;
    const tab = normalizeAccountingTab(tabs[0]);
    if (profile === 'P18' || platform) return tab !== null && ['ledger','outstanding','groups','vouchers','pnl','balance','daybook','trial','bankreco','costcentres','gstreturns','budgets'].includes(tab);
    return tab !== null && allowed.has(`/accounting?tab=${tab}`);
  }
  if (allowed.has(path)) return true;
  // Owners can use registered tenant workflows, but never the cross-tenant platform screens.
  if (profile === 'P18' || platform) {
    return REGISTERED_APP_ROUTES.some(p => !p.includes('*') && routePatternMatches(p, path));
  }
  const denied = (profile && PROFILE_DENIED_WORKFLOW_ROUTES[profile]) || [];
  if (denied.some(pattern => routePatternMatches(pattern, path))) return false;
  const patterns = [...allowed].flatMap(menu => PROFILE_WORKFLOW_ROUTES[menu] ?? []);
  if (patterns.includes(path)) return true;
  // A named neighboring route (e.g. /leads/triage or /service-tickets/new) is not an ID.
  if (STATIC_ROUTES.has(path)) return false;
  return patterns.some(pattern => routePatternMatches(pattern, path));
}

export function userLanding(user: ProfileUser | null | undefined): string {
  if (resolveProfile(user) || isPlatformAdmin(user) || !isLegacyIdentity(user)) return profileUserLanding(user);
  const role = resolveRoleKey(user);
  const dedicated: Partial<Record<typeof role, string>> = { LEAD_MANAGER: '/lead-manager', PRODUCTION_HEAD: '/manufacturing/dashboard', INVENTORY_HEAD: '/warehouse/dashboard', PURCHASE_HEAD: '/purchase-dashboard' };
  return dedicated[role] ?? '/dashboard';
}
